import React from 'react';
import {
	Typography,
	InputAdornment,
	FormControl,
	InputLabel,
	MenuItem,
	Box,
	TableHead,
	TableBody,
} from '@mui/material';
import { SearchRounded, CloseRounded } from '@mui/icons-material';
import ThreeDotMenu from './ThreeDotMenu';
import { EResource } from '../../types/departamento';
import { IRecursoRevision, ERevisionStatus } from '../../types/miRecurso';
import {
	PageContainer,
	FiltersContainer,
	SearchField,
	FilterSelect,
	AppliedFiltersContainer,
	FilterChip,
	ClearFiltersButton,
	ResultsHeader,
	StyledTableContainer,
	StyledTable,
	StyledTableHeaderCell,
	StyledTableCell,
	StyledTableRow,
	FileNameCell,
	FileIconContainer,
	UserCell,
	UserAvatar,
	StatusBadge,
	EmptyStateContainer,
} from './styles';
interface RevisionRecursosPageProps {
	recursos: IRecursoRevision[];
	materias: { id: number; name: string }[];
	loadError: string | null;
	searchQuery: string;
	onSearchChange: (query: string) => void;
	selectedFileType: EResource | 'all';
	onFileTypeChange: (fileType: EResource | 'all') => void;
	selectedStatus: ERevisionStatus | 'all';
	onStatusChange: (status: ERevisionStatus | 'all') => void;
	selectedMateria: number | 'all';
	onMateriaChange: (materia: number | 'all') => void;
	dateRange: string;
	onDateRangeChange: (range: string) => void;
	onResetFilters: () => void;
	onDownload: (recurso: IRecursoRevision) => void;
	onDenegar: (recurso: IRecursoRevision) => void;
	onSolicitarCambios: (recurso: IRecursoRevision) => void;
	onAprobar: (recurso: IRecursoRevision) => void;
}

const getStatusLabel = (status: ERevisionStatus | 'all'): string => {
	const statusMap: Record<string, string> = {
		all: 'Todos los estatus',
		aprobado: 'Aprobado',
		en_revision: 'En revisión',
		cambios_solicitados: 'Cambios solicitados',
		denegado: 'Denegado',
	};
	return statusMap[status] ?? status;
};

const getFileTypeLabel = (type: EResource | 'all'): string => {
	const typeMap: Record<string, string> = {
		all: 'Todos los tipos',
		pdf: 'PDF',
		docx: 'Word',
		pptx: 'PowerPoint',
		xlsx: 'Excel',
		video: 'Video',
		imagen: 'Imagen',
		other: 'Otro',
	};
	return typeMap[type] ?? type;
};

const getDateRangeLabel = (range: string): string => {
	const rangeMap: Record<string, string> = {
		'últimos-7-días': 'Últimos 7 días',
		'últimos-30-días': 'Últimos 30 días',
		'últimos-90-días': 'Últimos 90 días',
	};
	return rangeMap[range] ?? range;
};

const RevisionRecursosPage: React.FC<RevisionRecursosPageProps> = ({
	recursos,
	materias,
	searchQuery,
	onSearchChange,
	selectedFileType,
	onFileTypeChange,
	selectedStatus,
	onStatusChange,
	selectedMateria,
	onMateriaChange,
	dateRange,
	onDateRangeChange,
	onResetFilters,
	onDownload,
	onDenegar,
	onSolicitarCambios,
	onAprobar,
	loadError,
}) => {
	const getInitials = (name: string) => {
		const parts = name.split(' ');
		if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
		return name.substring(0, 2).toUpperCase();
	};

	const formatDate = (date: Date) =>
		date.toLocaleDateString('es-MX', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
		});

	const hasActiveFilters =
		searchQuery !== '' ||
		selectedFileType !== 'all' ||
		selectedStatus !== 'all' ||
		selectedMateria !== 'all' ||
		dateRange !== 'últimos-30-días';

	const appliedFilters: { label: string; onDelete: () => void }[] = [];
	if (selectedFileType !== 'all') {
		appliedFilters.push({
			label: getFileTypeLabel(selectedFileType),
			onDelete: () => onFileTypeChange('all'),
		});
	}
	if (selectedStatus !== 'all') {
		appliedFilters.push({
			label: getStatusLabel(selectedStatus),
			onDelete: () => onStatusChange('all'),
		});
	}
	if (selectedMateria !== 'all') {
		const materia = materias.find((m) => m.id === selectedMateria);
		appliedFilters.push({
			label: materia?.name ?? String(selectedMateria),
			onDelete: () => onMateriaChange('all'),
		});
	}
	if (dateRange !== 'últimos-30-días') {
		appliedFilters.push({
			label: getDateRangeLabel(dateRange),
			onDelete: () => onDateRangeChange('últimos-30-días'),
		});
	}

	return (
		<PageContainer>
			{loadError && (
				<Typography color='error' variant='body2'>
					{loadError}
				</Typography>
			)}
			{/* Filters */}
			<FiltersContainer>
				<SearchField
					fullWidth
					placeholder='Buscar por nombre de archivo o profesor...'
					value={searchQuery}
					onChange={(e) => onSearchChange(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>
								<SearchRounded />
							</InputAdornment>
						),
					}}
				/>

				<FormControl fullWidth>
					<InputLabel id='date-range-label'>Rango de fechas</InputLabel>
					<FilterSelect
						labelId='date-range-label'
						value={dateRange}
						label='Rango de fechas'
						onChange={(e) => onDateRangeChange(e.target.value as string)}
					>
						<MenuItem value='últimos-7-días'>Últimos 7 días</MenuItem>
						<MenuItem value='últimos-30-días'>Últimos 30 días</MenuItem>
						<MenuItem value='últimos-90-días'>Últimos 90 días</MenuItem>
					</FilterSelect>
				</FormControl>

				<FormControl fullWidth>
					<InputLabel id='file-type-label'>Tipo de archivo</InputLabel>
					<FilterSelect
						labelId='file-type-label'
						value={selectedFileType}
						label='Tipo de archivo'
						onChange={(e) => onFileTypeChange(e.target.value as EResource | 'all')}
					>
						<MenuItem value='all'>Todos los tipos</MenuItem>
						<MenuItem value={EResource.pdf}>PDF</MenuItem>
						<MenuItem value={EResource.docx}>Word</MenuItem>
						<MenuItem value={EResource.pptx}>PowerPoint</MenuItem>
						<MenuItem value={EResource.xlsx}>Excel</MenuItem>
						<MenuItem value={EResource.video}>Video</MenuItem>
						<MenuItem value={EResource.imagen}>Imagen</MenuItem>
					</FilterSelect>
				</FormControl>

				<FormControl fullWidth>
					<InputLabel id='status-label'>Estatus</InputLabel>
					<FilterSelect
						labelId='status-label'
						value={selectedStatus}
						label='Estatus'
						onChange={(e) => onStatusChange(e.target.value as ERevisionStatus | 'all')}
					>
						<MenuItem value='all'>Todos los estatus</MenuItem>
						<MenuItem value='en_revision'>En revisión</MenuItem>
						<MenuItem value='cambios_solicitados'>Cambios solicitados</MenuItem>
						<MenuItem value='aprobado'>Aprobado</MenuItem>
						<MenuItem value='denegado'>Denegado</MenuItem>
					</FilterSelect>
				</FormControl>

				<FormControl fullWidth>
					<InputLabel id='materia-label'>Materia</InputLabel>
					<FilterSelect
						labelId='materia-label'
						value={selectedMateria}
						label='Materia'
						onChange={(e) =>
							onMateriaChange(e.target.value === 'all' ? 'all' : Number(e.target.value))
						}
					>
						<MenuItem value='all'>Todas las materias</MenuItem>
						{materias.map((materia) => (
							<MenuItem key={materia.id} value={materia.id}>
								{materia.name}
							</MenuItem>
						))}
					</FilterSelect>
				</FormControl>
			</FiltersContainer>

			{/* Applied Filters */}
			{hasActiveFilters && (
				<AppliedFiltersContainer>
					{appliedFilters.map((filter, index) => (
						<FilterChip
							key={index}
							label={filter.label}
							onDelete={filter.onDelete}
							deleteIcon={<CloseRounded />}
						/>
					))}
					<ClearFiltersButton onClick={onResetFilters}>
						<CloseRounded fontSize='small' />
						<span>Limpiar filtros</span>
					</ClearFiltersButton>
				</AppliedFiltersContainer>
			)}

			{/* Results Header */}
			<ResultsHeader>
				<Typography variant='h6' color='text.primary'>
					{recursos.length.toLocaleString('es-MX')} archivo{recursos.length !== 1 ? 's' : ''}{' '}
					encontrado{recursos.length !== 1 ? 's' : ''}
				</Typography>
			</ResultsHeader>

			{/* Table */}
			{recursos.length > 0 ? (
				<StyledTableContainer>
					<StyledTable>
						<TableHead>
							<StyledTableRow>
								<StyledTableHeaderCell>Nombre de archivo</StyledTableHeaderCell>
								<StyledTableHeaderCell>Profesor</StyledTableHeaderCell>
								<StyledTableHeaderCell>Materia</StyledTableHeaderCell>
								<StyledTableHeaderCell>Tipo</StyledTableHeaderCell>
								<StyledTableHeaderCell>Fecha de subida</StyledTableHeaderCell>
								<StyledTableHeaderCell>Peso</StyledTableHeaderCell>
								<StyledTableHeaderCell>Estatus</StyledTableHeaderCell>
								<StyledTableHeaderCell align='center'>Acciones</StyledTableHeaderCell>
							</StyledTableRow>
						</TableHead>
						<TableBody>
							{recursos.map((recurso) => {
								const professorName = recurso.teacherName || 'Profesor desconocido';

								return (
									<StyledTableRow key={recurso.id}>
										{/* Nombre de archivo */}
										<StyledTableCell>
											<FileNameCell>
												<FileIconContainer resourcetype={recurso.resourceType}>
													{recurso.resourceType.toUpperCase().slice(0, 3)}
												</FileIconContainer>
												<Typography variant='body2' fontWeight={500} noWrap title={recurso.name}>
													{recurso.name}
												</Typography>
											</FileNameCell>
										</StyledTableCell>

										{/* Profesor */}
										<StyledTableCell>
											<UserCell>
												<UserAvatar>{getInitials(professorName)}</UserAvatar>
												<Box>
													<Typography variant='body2' fontWeight={500} noWrap>
														{professorName}
													</Typography>
													<Typography variant='caption' color='text.secondary'>
														{recurso.subjectName}
													</Typography>
												</Box>
											</UserCell>
										</StyledTableCell>

										{/* Materia */}
										<StyledTableCell>
											<Typography variant='body2' color='text.secondary' noWrap>
												{recurso.subjectName}
											</Typography>
										</StyledTableCell>

										{/* Tipo de archivo */}
										<StyledTableCell>
											<Typography variant='body2' color='text.secondary'>
												{getFileTypeLabel(recurso.resourceType)}
											</Typography>
										</StyledTableCell>

										{/* Fecha de subida */}
										<StyledTableCell>
											<Typography variant='body2' color='text.secondary' noWrap>
												{formatDate(recurso.uploadDate)}
											</Typography>
										</StyledTableCell>

										{/* Peso */}
										<StyledTableCell>
											<Typography variant='body2' color='text.secondary'>
												{recurso.size}
											</Typography>
										</StyledTableCell>

										{/* Estatus */}
										<StyledTableCell>
											<StatusBadge status={recurso.status}>
												{getStatusLabel(recurso.status)}
											</StatusBadge>
										</StyledTableCell>

										{/* Acciones */}
										<StyledTableCell align='center'>
											<ThreeDotMenu
												recurso={recurso}
												onDownload={onDownload}
												onSolicitarCambios={onSolicitarCambios}
												onAprobar={onAprobar}
												onDenegar={onDenegar}
											/>
										</StyledTableCell>
									</StyledTableRow>
								);
							})}
						</TableBody>
					</StyledTable>
				</StyledTableContainer>
			) : (
				<EmptyStateContainer>
					<Typography variant='h6' color='text.secondary' gutterBottom>
						No se encontraron archivos
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Intenta ajustar los filtros de búsqueda para ver más resultados.
					</Typography>
				</EmptyStateContainer>
		)}
	</PageContainer>
	);
};

export default RevisionRecursosPage;
